import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

const SECTION_HEADER_STYLE = {
  backgroundColor: "#eceeee",
  cursor: "default",
};

const SECTION_BODY_STYLE = { backgroundColor: "#eceeee" };

function SkeletonBar({ width = "100%", height = 14, className = "mb-2" }) {
  return (
    <p className={`placeholder-glow ${className} mb-0`}>
      <span
        className="placeholder col-12 rounded"
        style={{ width, height, display: "inline-block", minHeight: height }}
      />
    </p>
  );
}

function SkeletonLabel() {
  return <SkeletonBar width="45%" height={12} className="mb-2" />;
}

function SkeletonInput({ tall = false } = {}) {
  return (
    <SkeletonBar
      width="100%"
      height={tall ? 38 : 34}
      className="mb-0"
    />
  );
}

function SkeletonField({ tall = false, md = 6, lg = 4 }) {
  return (
    <Col md={md} lg={lg}>
      <SkeletonLabel />
      <SkeletonInput tall={tall} />
    </Col>
  );
}

function SkeletonSectionCard({ title, children }) {
  return (
    <Card className="mb-3 worker-profile-skeleton-card">
      <CardHeader
        className="d-flex justify-content-between align-items-center"
        style={SECTION_HEADER_STYLE}
      >
        <h6 className="mb-0 fw-bolder">{title}</h6>
        <p className="placeholder-glow mb-0">
          <span
            className="placeholder rounded-circle"
            style={{ width: 20, height: 20, display: "inline-block" }}
          />
        </p>
      </CardHeader>
      <CardBody style={SECTION_BODY_STYLE}>{children}</CardBody>
    </Card>
  );
}

function SkeletonFileUpload() {
  return (
    <Col md={6}>
      <SkeletonLabel />
      <SkeletonBar width="100%" height={72} />
    </Col>
  );
}

function SkeletonCheckboxGroup({ count = 4 }) {
  return (
    <Col md={12}>
      <SkeletonLabel />
      <div className="d-flex gap-3 flex-wrap">
        {Array.from({ length: count }).map((_, i) => (
          <div className="d-flex align-items-center gap-2" key={i}>
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded"
                style={{ width: 18, height: 18, display: "inline-block" }}
              />
            </p>
            <SkeletonBar width={100 + (i % 3) * 20} height={14} className="mb-0" />
          </div>
        ))}
      </div>
    </Col>
  );
}

function PersonalInformationSkeleton() {
  return (
    <SkeletonSectionCard title="Personal Information">
      <Row className="gy-3 gx-4">
        <Col md={12} className="d-flex justify-content-center mb-2">
          <p className="placeholder-glow mb-0">
            <span
              className="placeholder rounded-circle"
              style={{ width: 120, height: 120, display: "inline-block" }}
            />
          </p>
        </Col>
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonField key={i} tall={i === 2 || i === 5} />
        ))}
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonInput tall />
        </Col>
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonField key={`addr-${i}`} md={6} lg={4} />
        ))}
        <SkeletonField md={6} lg={4} />
      </Row>
    </SkeletonSectionCard>
  );
}

function EmergencyContactSkeleton() {
  return (
    <SkeletonSectionCard title="Emergency Contact">
      <Row className="gy-3 gx-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonField key={i} md={6} lg={6} tall={i === 2} />
        ))}
      </Row>
    </SkeletonSectionCard>
  );
}

function RightToWorkSkeleton() {
  return (
    <SkeletonSectionCard title="Right to Work">
      <Row className="gy-3 gx-4">
        <SkeletonField md={6} lg={6} tall />
        <SkeletonField md={6} lg={6} />
        <SkeletonField md={6} lg={6} />
        <SkeletonField md={6} lg={6} />
        <SkeletonFileUpload />
        <SkeletonFileUpload />
        <SkeletonFileUpload />
        <SkeletonFileUpload />
      </Row>
    </SkeletonSectionCard>
  );
}

function EmploymentTaxSkeleton() {
  return (
    <SkeletonSectionCard title="Employment & Tax">
      <Row className="gy-3 gx-4 mb-2">
        <Col xs={12}>
          <div className="d-flex gap-4">
            <SkeletonBar width={80} height={18} />
            <SkeletonBar width={80} height={18} />
          </div>
        </Col>
      </Row>
      <Row className="gy-3 gx-4">
        <SkeletonFileUpload />
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonBar width="100%" height={80} />
        </Col>
      </Row>
    </SkeletonSectionCard>
  );
}

function BankDetailsSkeleton() {
  return (
    <SkeletonSectionCard title="Bank Details">
      <Row className="gy-3 gx-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonField key={i} md={6} lg={6} />
        ))}
      </Row>
    </SkeletonSectionCard>
  );
}

function MoreAboutYouSkeleton() {
  return (
    <SkeletonSectionCard title="More About You">
      <Row className="gy-3 gx-4">
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonBar width="100%" height={80} />
        </Col>
        <Col md={12}>
          <div className="d-flex gap-4 mb-2">
            <SkeletonBar width={140} height={18} />
            <SkeletonBar width={160} height={18} />
          </div>
        </Col>
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonBar width="100%" height={80} />
        </Col>
        <Col md={12}>
          <SkeletonLabel />
          <SkeletonBar width="100%" height={100} />
        </Col>
      </Row>
    </SkeletonSectionCard>
  );
}

function WorkPreferencesSkeleton() {
  return (
    <SkeletonSectionCard title="Work Preferences">
      <Row className="gy-3 gx-4">
        <Col md={12}>
          <SkeletonBar width="30%" height={16} className="mb-3" />
          <Row className="gy-3 gx-4 align-items-end">
            <Col md={5}>
              <SkeletonLabel />
              <SkeletonInput tall />
            </Col>
            <Col md={5}>
              <SkeletonLabel />
              <SkeletonInput tall />
            </Col>
            <Col md={2}>
              <SkeletonBar width="100%" height={34} />
            </Col>
          </Row>
          <hr className="my-3 border-secondary border-opacity-25" />
        </Col>
        <SkeletonField md={6} lg={6} tall />
        <SkeletonCheckboxGroup count={5} />
        <Col md={12}>
          <div className="d-flex align-items-center gap-2">
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded"
                style={{ width: 18, height: 18, display: "inline-block" }}
              />
            </p>
            <SkeletonBar width="55%" height={14} className="mb-0" />
          </div>
        </Col>
        <SkeletonCheckboxGroup count={7} />
        <SkeletonCheckboxGroup count={4} />
      </Row>
    </SkeletonSectionCard>
  );
}

function ComplianceSkeleton() {
  return (
    <SkeletonSectionCard title="Certificates & Compliance">
      <Row className="gy-3 gx-4">
        {[0, 1].map((row) => (
          <Col md={12} key={row}>
            <Row className="gy-3 gx-4 align-items-end">
              <Col md={4}>
                <SkeletonLabel />
                <SkeletonInput tall />
              </Col>
              <Col md={4}>
                <SkeletonLabel />
                <SkeletonBar width="100%" height={72} />
              </Col>
              <Col md={3}>
                <SkeletonLabel />
                <SkeletonInput />
              </Col>
              <Col md={1}>
                <SkeletonBar width="100%" height={34} />
              </Col>
            </Row>
            {row === 0 && <hr className="my-3 border-secondary border-opacity-25" />}
          </Col>
        ))}
        <Col md={12}>
          <SkeletonBar width={100} height={32} />
        </Col>
      </Row>
    </SkeletonSectionCard>
  );
}

function QrCodeSkeleton() {
  return (
    <Card className="border-0 mb-3">
      <CardBody className="p-4">
        <Row className="align-items-center">
          <Col xs={12} lg={6} className="mb-4 mb-lg-0">
            <SkeletonBar width="60%" height={20} className="mb-2" />
            <SkeletonBar width="85%" height={14} />
          </Col>
          <Col xs={12} lg={6} className="d-flex justify-content-lg-end justify-content-center">
            <p className="placeholder-glow mb-0">
              <span
                className="placeholder rounded"
                style={{ width: 160, height: 160, display: "inline-block" }}
              />
            </p>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}

export default function WorkerProfileSkeleton() {
  return (
    <div className="worker-profile-skeleton" aria-busy="true" aria-label="Loading profile">
      <p className="placeholder-glow mb-3">
        <span className="placeholder col-12 rounded" style={{ height: 52 }} />
      </p>
      <QrCodeSkeleton />
      <PersonalInformationSkeleton />
      <EmergencyContactSkeleton />
      <RightToWorkSkeleton />
      <EmploymentTaxSkeleton />
      <BankDetailsSkeleton />
      <MoreAboutYouSkeleton />
      <WorkPreferencesSkeleton />
      <ComplianceSkeleton />
      <div className="d-grid">
        <p className="placeholder-glow mb-0">
          <span className="placeholder col-12 rounded" style={{ height: 42 }} />
        </p>
      </div>
    </div>
  );
}
